import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useEffect, useState } from "react";
import { Loader2, ArrowRight, ExternalLink } from "lucide-react";
import { useTheme } from "@/lib/theme-context";
import { z } from "zod";
import { decryptUrl } from "@/lib/safelink-crypto";
import { logSafelink } from "@/lib/safelink-log";

const search = z.object({ to: z.string().optional(), t: z.string().optional() });
export const Route = createFileRoute("/redirect")({
  validateSearch: search,
  head: () => ({
    meta: [
      { title: "إعادة التوجيه — Rawan UI" },
      { name: "description", content: "جارٍ إعادة توجيهك…" },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "/redirect" }],
  }),
  component: RedirectPage,
});
function RedirectPage() {
  const { to, t: token } = Route.useSearch();
  const { dir, language } = useTheme();
  const isAr = dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const [count, setCount] = useState(5);
  const [target, setTarget] = useState<string>(to || "/");

  useEffect(() => {
    let cancelled = false;
    if (token) {
      decryptUrl(token)
        .then((u) => {
          if (cancelled) return;
          setTarget(u);
          logSafelink({ source: "redirect", status: "success" });
        })
        .catch(() => {
          logSafelink({ source: "redirect", status: "decrypt-failed" });
        });

    } else if (to) {
      setTarget(to);
    }
    return () => { cancelled = true; };
  }, [token, to]);

  useEffect(() => {
    if (count <= 0) { window.location.href = target; return; }
    const i = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(i);
  }, [count, target]);
  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12 max-w-xl grid place-items-center">
        <div className="w-full bg-card border border-border rounded-2xl p-8 text-center shadow-card animate-scale-in">
          <div className="grid place-items-center w-16 h-16 mx-auto rounded-2xl animate-pulse-glow" style={{ background: "var(--gradient-primary)" }}>
            <ArrowRight className={`h-8 w-8 text-primary-foreground ${(language === "ar" ? "rotate-180" : "")}`} />
          </div>
          <h1 className="text-2xl font-extrabold mt-4">
            {t(`جارٍ إعادة التوجيه خلال ${count} ث…`, `Redirecting in ${count}s…`)}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 break-all">{target}</p>
          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            {t("الرجاء عدم إغلاق الصفحة", "Please don't close the page")}
          </div>
          <a href={target} className="btn-primary inline-flex items-center gap-2 mt-6">
            <ExternalLink className="h-4 w-4" />
            {t("الذهاب الآن", "Go now")}
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
